import AccessBlastRadius from './AccessBlastRadius'
import { Card, Kpi, naira, orgMrr, planDef, orgAccess } from '../ui'
import { PLANS } from '../../hooks/useBilling'
import { timeAgo } from '../../utils/timeAgo'

const BAR_COLORS = { starter: 'bg-gray-400', growth: 'bg-blue-500', enterprise: 'bg-teal-500' }

// Paystack event type → display style. Unknown types fall back to gray.
const EVENT_STYLE = {
  'charge.success': { label: 'Payment received', cls: 'bg-green-100 text-green-700' },
  'subscription.create': { label: 'New subscription', cls: 'bg-teal-100 text-teal-700' },
  'invoice.payment_failed': { label: 'Payment failed', cls: 'bg-red-100 text-red-700' },
  'subscription.disable': { label: 'Subscription cancelled', cls: 'bg-gray-200 text-gray-600' },
  'subscription.not_renew': { label: 'Won\'t renew', cls: 'bg-amber-100 text-amber-700' },
}

export default function Billing({ organizations, events, users = [] }) {
  const mrr = organizations.reduce((s, o) => s + orgMrr(o), 0)
  const paying = organizations.filter((o) => o.subscriptionStatus === 'active')
  const pastDue = organizations.filter((o) => o.subscriptionStatus === 'past_due')
  const atRisk = pastDue.reduce((s, o) => s + (planDef(o.plan)?.amount ?? 0), 0)
  const trialing = organizations.filter((o) => ['trialing', 'ending', 'expired'].includes(orgAccess(o).key))

  const counts = PLANS.map((p) => ({
    ...p,
    n: organizations.filter((o) => (o.plan || 'starter') === p.tier).length,
  }))
  const max = Math.max(1, ...counts.map((c) => c.n))

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Kpi label="MRR" value={naira(mrr)} sub={`${paying.length} paying organization(s)`} />
        <Kpi label="At risk" value={naira(atRisk)} valueClass={atRisk > 0 ? 'text-red-600' : 'text-gray-800'}
          sub={`${pastDue.length} org(s) past due`} />
        <Kpi label="Trialing" value={trialing.length} sub="orgs on a trial (incl. expired)" />
      </div>

      <AccessBlastRadius organizations={organizations} users={users} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card title="Plan distribution">
          <div className="px-4 py-3 space-y-2.5">
            {counts.map((c) => (
              <div key={c.tier} className="flex items-center gap-3 text-sm">
                <span className="w-20 text-xs font-semibold text-gray-500 capitalize">{c.tier}</span>
                <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                  <div className={`h-full rounded-full ${BAR_COLORS[c.tier]}`} style={{ width: `${(c.n / max) * 100}%` }} />
                </div>
                <span className="w-14 text-right text-xs text-gray-500 tabular-nums">{c.n} org{c.n !== 1 ? 's' : ''}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Payment events" hint="live from the Paystack webhook">
          {(events || []).length === 0 ? (
            <p className="px-4 py-6 text-sm text-gray-400">
              No events logged yet. Events appear here as Paystack delivers webhooks (charges,
              failures, cancellations) — after running phase3_email_events_migration.sql and
              redeploying the paystack-webhook function.
            </p>
          ) : (
            <ul className="max-h-80 overflow-y-auto">
              {events.map((ev) => {
                const style = EVENT_STYLE[ev.eventType] || { label: ev.eventType, cls: 'bg-gray-100 text-gray-600' }
                const org = organizations.find((o) => o.id === ev.organizationId)
                return (
                  <li key={ev.id} className="flex items-center gap-3 px-4 py-2.5 border-b border-gray-100 last:border-0">
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${style.cls}`}>{style.label}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800 truncate">{org?.name || ev.customerEmail || 'Unknown customer'}</p>
                    </div>
                    {ev.amount != null && <span className="text-xs font-semibold tabular-nums text-gray-700">{naira(ev.amount)}</span>}
                    <span className="text-[11px] text-gray-400 flex-none">{timeAgo(ev.createdAt)}</span>
                  </li>
                )
              })}
            </ul>
          )}
        </Card>
      </div>
    </div>
  )
}
